import { queryOptions, useQuery } from '@tanstack/react-query';

import { ApiResponse, QueryOptions } from '@cast/shared-types';

import { apiClient } from '@cast/infrastructure-api';

const PRICING_CAST_ANYWHERE_CLUSTER_COSTS_QUERY_KEY =
  'pricing-cast-anywhere-cluster-costs';

export const getPricingCastAnywhereClusterCostsQueryKey = (
  organizationId?: string,
  clusterId?: string
) => {
  if (!organizationId) {
    return [PRICING_CAST_ANYWHERE_CLUSTER_COSTS_QUERY_KEY] as const;
  }
  if (!clusterId) {
    return [
      PRICING_CAST_ANYWHERE_CLUSTER_COSTS_QUERY_KEY,
      organizationId,
    ] as const;
  }
  return [
    PRICING_CAST_ANYWHERE_CLUSTER_COSTS_QUERY_KEY,
    organizationId,
    clusterId,
  ] as const;
};

const apiFn = apiClient.pricing.getCastAnywhereClusterPricing;

type CastAnywhereClusterPricingQueryData = ApiResponse<typeof apiFn>;

type CastAnywhereClusterPricingQueryArgs<SELECT> = QueryOptions<
  CastAnywhereClusterPricingQueryData,
  SELECT
> & {
  organizationId?: string;
  clusterId?: string;
};

const getCastAnywhereClusterPricingQueryOptions = ({
  organizationId,
  clusterId,
  retry,
  enabled = true,
}: Pick<
  CastAnywhereClusterPricingQueryArgs<unknown>,
  'organizationId' | 'clusterId' | 'retry' | 'enabled'
>) =>
  queryOptions({
    queryKey: getPricingCastAnywhereClusterCostsQueryKey(
      organizationId,
      clusterId
    ),
    queryFn: async () => {
      const { data } = await apiFn({
        organizationId: organizationId!,
        clusterId: clusterId!,
      });
      return data;
    },
    retry,
    enabled: !!organizationId && !!clusterId && enabled,
  });

export const useCastAnywhereClusterPricingQuery = <
  SELECT = CastAnywhereClusterPricingQueryData
>({
  organizationId,
  clusterId,
  enabled = true,
  // Don't try to refetch if error is 404. It means that pricing was not entered yet.
  retry = (failureCount, error) => error.code !== '404' && failureCount < 1,
  select,
}: CastAnywhereClusterPricingQueryArgs<SELECT>) => {
  const { isLoading, data, error, refetch } = useQuery({
    ...getCastAnywhereClusterPricingQueryOptions({
      organizationId,
      clusterId,
      enabled,
      retry,
    }),
    select,
  });

  return {
    clusterPricing: data,
    isLoading,
    error,
    refetch,
  };
};
